import React, { useCallback, useEffect, useMemo, useState } from "react";
import { clinicalSummaryApi } from "../../redux/service/clinicalSummary_service";
import { AppListing } from "../../components/appListing";
import MainPageDialog from "../../components/mainPageDialog";
import moment from "moment";
import { toast } from "react-toastify";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from "react-icons/md";

function ClinicalSummaryLogsModal({ setShowClinicalSummaryLogsModal }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const columns = useMemo(
    () => [
      {
        title: "Date",
        key: "action_datetime",
        getData: (item) =>
          moment(item.action_datetime).format("MM/DD/YYYY hh:mm:ss A"),
      },
      { title: "User", key: "name" },
      {
        title: "Action",
        key: "action",
        getData: (item) => (
          <span className="capitalize">
            {item.action === "transmit" ? (
              <>{item.action + " - " + item.action_data}</>
            ) : (
              <>{item.action}</>
            )}
          </span>
        ),
      },
    ],
    []
  );

  const [fetchClinicalSummaryLogsTrigger] =
    clinicalSummaryApi.endpoints.getClinicalSummaryLogs.useLazyQuery();

  const [loading, setLoading] = useState(false);
  const [clinicalSummaryLogs, setClinicalSummaryLogs] = useState([]);

  const handleFetchCinicalSummaryLogs = useCallback(() => {
    setLoading(true);
    fetchClinicalSummaryLogsTrigger({ page: currentPage })
      .then((response) => {
        if (response?.data) {
          setClinicalSummaryLogs(response?.data || []);
        } else {
          setClinicalSummaryLogs([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching clinical summary logs", error);
        toast.error("Error fetching clinical summary logs");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentPage, fetchClinicalSummaryLogsTrigger]);

  useEffect(() => {
    handleFetchCinicalSummaryLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const footerList = [
    {
      title: "Close",
      type: "cancel",
      action: () => {
        setShowClinicalSummaryLogsModal(false);
      },
    },
  ];

  useEffect(() => {
    if (clinicalSummaryLogs?.data?.length > 0) {
      setTotalPages(
        Math.ceil(
          clinicalSummaryLogs?.meta?.total / clinicalSummaryLogs?.meta?.limit
        )
      );
    }
  }, [clinicalSummaryLogs]);

  return (
    <MainPageDialog
      title="Clinical Summary Logs"
      modalSize="large"
      maxHeight={true}
      footerList={footerList}
      onCancelHandler={() => setShowClinicalSummaryLogsModal(false)}
    >
      <div className={`flex-auto bg-white justify-start p-3 text-start `}>
        <AppListing
          loading={loading}
          listData={clinicalSummaryLogs?.data || []}
          cols={columns}
        />
      </div>
      {totalPages > 0 && (
        <div className="flex w-[100%] justify-end items-center mt-5 gap-3 text-[16px]">
          <span
            className={currentPage > 1 && `cursor-pointer`}
            role="button"
            tabIndex={0}
            onClick={() => {
              currentPage > 1 && setCurrentPage(1);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                currentPage > 1 && setCurrentPage(1);
              }
            }}
          >
            <MdKeyboardDoubleArrowLeft
              color={!(currentPage > 1) && "#d3d3d3"}
            />
          </span>
          <span
            className={currentPage > 1 && `cursor-pointer`}
            role="button"
            tabIndex={0}
            onClick={() => {
              currentPage > 1 && setCurrentPage(currentPage - 1);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                currentPage > 1 && setCurrentPage(currentPage - 1);
              }
            }}
          >
            <MdKeyboardArrowLeft color={!(currentPage > 1) && "#d3d3d3"} />
          </span>
          {Array.from({ length: totalPages }, (_, i) => {
            const page = i + 1;
            if (
              page === 1 ||
              page === totalPages ||
              (page >= currentPage - 1 && page <= currentPage + 1)
            ) {
              return (
                <span
                  key={i}
                  className={`py-[0.1875rem] px-[0.625rem] ${
                    currentPage === page && "bg-[#2199e8]"
                  } ${
                    currentPage === page ? "text-white" : "text-[#2199e8]"
                  } cursor-pointer`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </span>
              );
            } else if (page === currentPage - 2 || page === currentPage + 2) {
              return (
                <span key={i} className="px-[0.625rem]">
                  ...
                </span>
              );
            }
            return null;
          })}
          <span
            className={currentPage < totalPages && `cursor-pointer`}
            role="button"
            tabIndex={0}
            onClick={() => {
              currentPage < totalPages && setCurrentPage(currentPage + 1);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                currentPage < totalPages && setCurrentPage(currentPage + 1);
              }
            }}
          >
            <MdKeyboardArrowRight
              color={!(currentPage < totalPages) && "#d3d3d3"}
            />
          </span>
          <span
            className={currentPage < totalPages && `cursor-pointer`}
            role="button"
            tabIndex={0}
            onClick={() => {
              currentPage < totalPages && setCurrentPage(totalPages);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                currentPage < totalPages && setCurrentPage(totalPages);
              }
            }}
          >
            <MdKeyboardDoubleArrowRight
              color={!(currentPage < totalPages) && "#d3d3d3"}
            />
          </span>
        </div>
      )}
    </MainPageDialog>
  );
}

export default ClinicalSummaryLogsModal;
